/**
 * PDF Lab types and the two conversions the UI cannot get wrong.
 *
 * OpenDataLoader returns a TREE, not a list: a table contains rows, a row
 * contains cells, a cell contains paragraphs, and a list contains list items
 * under a different key again. Flattening it in one place means the overlay,
 * the outline and the node inspector are all looking at the same nodes.
 */

export interface OptionDef {
  name: string;
  python_name: string;
  short_name: string | null;
  type: "string" | "boolean";
  required: boolean;
  default: string | boolean | null;
  description: string;
}

export interface OptionCatalogue {
  version: string;
  options: OptionDef[];
  reserved: string[];
  hybridConfigured: boolean;
  hybridSettingKeys: string[];
}

export interface EnginePart {
  id: "core" | "hybrid";
  label: string;
  phase: "off" | "starting" | "ready" | "error";
  replicas: number;
  readyReplicas: number;
  detail: string;
}

/** One node of the parse tree, flattened. */
export interface FlatNode {
  id: number;
  type: string;
  tag?: string;
  page: number;
  /** [x1, y1, x2, y2] in PDF points, origin BOTTOM left. */
  bbox: [number, number, number, number] | null;
  text: string;
  depth: number;
  font?: string;
  fontSize?: number;
  /** Rows x columns, tables only. */
  shape?: string;
}

export interface ConvertOutputs {
  json?: any;
  markdown?: string;
  html?: string;
  text?: string;
  annotatedPdf?: string;
  taggedPdf?: string;
}

export interface ConvertResult {
  ok: boolean;
  ms: number;
  version?: string;
  returncode?: number;
  command?: string[];
  outputs?: ConvertOutputs;
  images?: Array<{ name: string; bytes: number; base64?: string; skipped?: string }>;
  log?: string;
  stdout?: string;
  error?: string;
}

/**
 * Options whose value is a LIST, sent comma-separated.
 *
 * Read from the option's own description where possible, so a future flag that
 * takes a list gets a multi-select automatically. `content-safety-off` is named
 * explicitly because upstream's metadata does not mark it: its description
 * lists six values without ever saying comma-separated, while the Python
 * signature types it `Union[str, List[str]]`. Detection alone would render it
 * as a single choice and quietly make five of the six filters unreachable.
 */
const KNOWN_MULTI = new Set(["content-safety-off"]);

export const isMultiOption = (o: OptionDef) =>
  /comma[- ]separated/i.test(o.description) || KNOWN_MULTI.has(o.name);

/**
 * The permitted values of an enum-ish option, read out of its description.
 *
 * The generated catalogue writes them as `Values: a, b, c.` every time, which
 * makes this parse reliable and — more importantly — self-updating: pinning a
 * new engine version changes the controls without anyone editing this file.
 * Anything that does not match falls back to a free-text input, which is the
 * safe direction to be wrong in.
 */
export function enumValues(o: OptionDef): string[] | null {
  const m = o.description.match(/Values:\s*([^.]+?)(?:\.\s|\.$|$)/);
  if (!m) return null;
  const values = m[1]
    // Parentheticals are stripped BEFORE the split, not after. Several
    // descriptions gloss a value as `auto (default, dynamic triage)` — the
    // comma inside those brackets would otherwise split one value into two
    // fragments and the option would silently fall back to a text box.
    .replace(/\([^)]*\)/g, "")
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v && !v.includes(" "));
  return values.length >= 2 ? values : null;
}

/** The short label the UI shows for an option. */
export const optionLabel = (o: OptionDef) =>
  o.name.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/**
 * Flatten the parse tree.
 *
 * The three container shapes are not interchangeable and each has its own key:
 * `kids` for the general case, `rows`/`cells` for tables, `list items` for
 * lists. Missing one of them does not error — it silently drops every cell in
 * every table, which reads as "the parser found no table content".
 */
export function flattenNodes(root: any): FlatNode[] {
  const out: FlatNode[] = [];

  const visit = (node: any, depth: number) => {
    if (!node || typeof node !== "object") return;

    if (node.type) {
      const bb = node["bounding box"];
      out.push({
        id: node.id ?? out.length,
        type: node.type,
        tag: node.pdfua_tag,
        page: node["page number"] ?? 1,
        bbox:
          Array.isArray(bb) && bb.length === 4
            ? [Number(bb[0]), Number(bb[1]), Number(bb[2]), Number(bb[3])]
            : null,
        text: typeof node.content === "string" ? node.content : "",
        depth,
        font: node.font,
        fontSize: node["font size"],
        shape:
          node["number of rows"] !== undefined
            ? `${node["number of rows"]}x${node["number of columns"]}`
            : undefined,
      });
    }

    const next = depth + (node.type ? 1 : 0);
    for (const kid of node.kids ?? []) visit(kid, next);
    for (const row of node.rows ?? []) visit(row, next);
    for (const cell of node.cells ?? []) visit(cell, next);
    for (const item of node["list items"] ?? []) visit(item, next);
  };

  visit(root, 0);
  return out;
}

/**
 * A bounding box as CSS percentages of the page.
 *
 * TWO conversions, and getting either wrong puts every box in the wrong place:
 * the values are in POINTS (not the pixels of the rendered image, which is at
 * 200 DPI), and PDF's origin is at the BOTTOM left, so the top of a box is
 * `pageHeight - y2`.
 */
export function bboxToPercent(
  bbox: [number, number, number, number],
  pointsWidth: number,
  pointsHeight: number,
) {
  const [x1, y1, x2, y2] = bbox;
  return {
    left: (x1 / pointsWidth) * 100,
    top: ((pointsHeight - y2) / pointsHeight) * 100,
    width: ((x2 - x1) / pointsWidth) * 100,
    height: ((y2 - y1) / pointsHeight) * 100,
  };
}

/** Colour per node type, so the overlay is readable without a legend lookup. */
export const NODE_COLOR: Record<string, string> = {
  table: "#b45309",
  "table cell": "#f59e0b",
  "table row": "#fbbf24",
  heading: "#7c3aed",
  paragraph: "#2563eb",
  "text block": "#0891b2",
  list: "#059669",
  "list item": "#10b981",
  image: "#db2777",
  caption: "#9333ea",
};
export const nodeColor = (type: string) => NODE_COLOR[type] ?? "#64748b";

export const prettyBytes = (n: number) =>
  n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
